import { fetchUtils } from 'react-admin';
import { stringifyOpts } from 'providers/clients/httpClient';
import { httpClient } from 'providers/clients/httpClient';
import { SyndicationAvailableChannelsResult } from 'utility/types/dataProvider';

// -----------------------------------------------
// ---------- WIRE/SYNDICATION PROVIDER ----------
// -----------------------------------------------

// Note: sometimes "uuid" comes in as a string json object
// and needs to be parsed eg: "{"external_id":"1234abcd",...}"
export type WireImportStatusData = {
  uuid: string;
  asset_uuid?: string;
  status?: string | null;
  message?: string | null;
};

export type WireImportStatusRtn = {
  status?: string | number;
  data?: WireImportStatusData[];
};

export const wireSyndicationProvider = {
  getAvailableChannels: async (
    cursor?: string | number,
  ): Promise<SyndicationAvailableChannelsResult> => {
    try {
      const url = `/wire/channel/`;
      const { data } = await httpClient({
        url: url,
        method: 'GET',
        params: cursor ? { page: cursor } : undefined,
      });
      return data; // Return the JSON payload as the default search
    } catch (error) {
      console.error('[dataProvider] getAvailableChannels() error:', error?.toString());
      throw error;
    }
  },

  getChannelConfigurations: async (channelId: string): Promise<any> => {
    try {
      const url = `/wire/channel/${channelId}/search/config/`;
      const { data } = await httpClient({
        url: url,
        method: 'GET',
      });
      return data; // Return the JSON payload as the default search
    } catch (error) {
      console.error(
        '[dataProvider] getChannelConfigurations() error:',
        error?.toString(),
      );
      throw error;
    }
  },

  importChannelAsset: async (
    channelId: string,
    objectId: string,
  ): Promise<WireImportStatusRtn> => {
    try {
      const payload = {
        object_id: objectId,
      };
      const url = `/wire/channel/${channelId}/import/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'POST',
        data: JSON.stringify(payload),
      });

      const formattedResult: WireImportStatusRtn = {
        status: data?.status || status,
        data: data?.data,
      };

      return formattedResult;
    } catch (error) {
      console.error('[importChannelAsset] Error:', error);
      throw error;
    }
  },

  getWireImportStatus: async (
    channelId: string,
    objects: string[],
  ): Promise<WireImportStatusRtn> => {
    try {
      // Extract the `external_id` or whatever ID you need from the parsed objects
      const objectIds = objects.map((obj) => obj);

      if (!objectIds || objectIds.length === 0) {
        throw new Error('Missing object_id array');
      }

      // Construct the payload to include object_id as an array
      const payload = {
        object_id: objectIds, // Send array of external_ids
      };

      const url = `/wire/channel/${channelId}/import/status/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: url,
        method: 'GET',
      });

      const formattedResult: WireImportStatusRtn = {
        status: data?.status || status,
        data: data?.data,
      };

      return formattedResult;
    } catch (error) {
      console.error('[getWireImportStatus] Error:', error);
      throw error;
    }
  },

  getCategoryMap: async (channelId: string | undefined): Promise<any> => {
    try {
      const url = `/wire/channel/${channelId}/map/`;

      const { data } = await httpClient({
        url: url,
        method: 'GET',
      });

      const formattedResult = {
        data: { ...data },
      };
      return formattedResult;
    } catch (error) {
      console.error('[dataProvider] getCategoryMap() error:', error?.toString());
      throw error;
    }
  },

  updateCategoryMap: async (channelId: string | undefined, map: any): Promise<any> => {
    try {
      const payload = {
        section_map: {
          ...map,
        },
      };
      const url = `/wire/channel/${channelId}/map/`;

      const { data } = await httpClient({
        url: url,
        method: 'PUT',
        data: JSON.stringify(payload),
      });

      const formattedResult = {
        data: { ...data },
      };
      return formattedResult;
    } catch (error) {
      console.error('[dataProvider] updateCategoryMap() error:', error?.toString());
      throw error;
    }
  },

  deleteCategoryMap: async (channelId: string | undefined): Promise<any> => {
    try {
      const url = `/wire/channel/${channelId}/map/`;

      const { data } = await httpClient({
        url: url,
        method: 'DELETE',
      });

      const formattedResult = {
        data: { ...data },
      };
      return formattedResult;
    } catch (error) {
      console.error('[dataProvider] deleteCategoryMap() error:', error?.toString());
      throw error;
    }
  },
};
